import axios from 'axios';
import type {
  CategoriesGroup,
  CategoriesGroupResponse,
  CategoriesParams,
  CategoriesResponse,
  Category,
  CategoryDetailResponse,
} from '../types/Category';
import { baseUrl, HttpMethods } from './base';

const categoryBasePath = `${baseUrl.SHERPA}/categories`;
const categoryV2BasePath = `${baseUrl.SHERPA}/categories-v2`;
const categoryGroupBasePath = `${baseUrl.SHERPA}/categoryGroups`;

interface CategoryPayload {
  name: string;
  description: string;
  parentId?: number;
  categoryTypeId: number;
}

export interface CreateCategoryPayload extends CategoryPayload {}

export interface CreateCategoryV2Payload extends CreateCategoryPayload {
  slug: string;
}

interface CategoryTypeResponse {
  data: CategoriesGroup;
}

type CategoryV2ListResponse = Category[] | { data: Category[] };
type CategoryV2CreateResponse = Category | { data: Category };

export const createCategory = async (data?: CreateCategoryPayload) => {
  const response = await axios.request<CategoriesResponse>({
    url: categoryBasePath,
    method: HttpMethods.POST,
    data,
  });
  return response.data;
};

export const createCategoryV2 = async (data: CreateCategoryV2Payload): Promise<Category> => {
  const response = await axios.request<CategoryV2CreateResponse>({
    url: categoryV2BasePath,
    method: HttpMethods.POST,
    data,
  });
  return 'data' in response.data ? response.data.data : response.data;
};

export const getCategoriesV2 = async (params?: CategoriesParams): Promise<Category[]> => {
  const response = await axios.request<CategoryV2ListResponse>({
    url: categoryV2BasePath,
    method: HttpMethods.GET,
    params,
  });
  return Array.isArray(response.data) ? response.data : response.data.data;
};

export const getCategories = async (params?: CategoriesParams) => {
  const response = await axios.request<CategoriesResponse>({
    url: categoryBasePath,
    method: HttpMethods.GET,
    params,
  });
  return response.data;
};

export const getCategory = async (id: number) => {
  const response = await axios.request<CategoryDetailResponse>({
    url: `${categoryBasePath}/${id}`,
    method: HttpMethods.GET,
  });
  return response.data;
};

export interface UpdateCategoryPayload {
  name: string;
  description: string;
  parentId: number;
  categoryTypeId: number;
}

export interface UpdateCategoryService {
  id: number;
  data: Partial<UpdateCategoryPayload>;
}

export const updateCategory = async ({ id, data }: UpdateCategoryService) => {
  const response = await axios.request({
    url: `${categoryBasePath}/${id}`,
    method: HttpMethods.PATCH,
    data,
  });
  return response.data;
};

export const getCategoriesGroup = async () => {
  const response = await axios.request<CategoriesGroupResponse>({
    url: `${categoryGroupBasePath}`,
    method: HttpMethods.GET,
  });
  return response.data;
};

export const ensureProgramCategoryType = async () => {
  const response = await axios.request<CategoryTypeResponse>({
    url: `${categoryGroupBasePath}/program`,
    method: HttpMethods.POST,
  });
  return response.data;
};

export const getCategoriesSubgroup = async (params: { [key: string]: string | number | undefined }) => {
  const response = await axios.request({
    url: `${categoryGroupBasePath}/subgroup`,
    method: HttpMethods.GET,
    params,
  });
  return response.data;
};

export const getCategoriesByGroup = async (id: number) => {
  const response = await axios.request({
    url: `${categoryBasePath}/${id}`,
    method: HttpMethods.GET,
  });
  return response.data;
};

export const postDefaultCategories = async () => {
  const res = await axios.request({
    method: HttpMethods.POST,
    url: `${categoryBasePath}/default`,
  });
  return res.data;
};
